import {
  ActionIcon, Alert, Badge, Button, Checkbox, FileInput, Grid, Group, Loader, MultiSelect, NumberInput, Paper, ScrollArea, Select,
  SimpleGrid, Skeleton, Stack, Stepper, Switch, Table, Text, Textarea, TextInput, ThemeIcon, Title, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconArrowBackUp, IconArrowLeft, IconArrowRight, IconBuildingCommunity, IconCheck, IconCircleCheck,
  IconCircleX, IconDeviceFloppy, IconDownload, IconFile, IconFileCertificate, IconHome2, IconInfoCircle, IconKey, IconMapPin,
  IconMessage, IconPencil, IconPlus, IconSend, IconShieldCheck, IconTrash, IconTrees, IconUpload,
} from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type Dispatch, type ReactNode, type SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { http } from "../../api/http";
import { login } from "../../auth/keycloak";
import { useUser } from "../../auth/user";
import { fmtDate, fmtDateTime, fmtNum } from "../../components/ui";
import { useT } from "../../i18n/script";
import { speciesColor, useSpeciesNames } from "../holdings/HoldingsPage";
import {
  type CaseData, type CaseDetail, type CaseEvent, CaseProgress, CaseStatusBadge, CasesStyle, CaseTimeline, errMsg, fmtArea, fmtSize,
  HOLDING_TYPE, lookupParcel, openDocument, OPEN_STATUSES, type OrchardIn, PARCEL_USE, ParcelMap, type ParcelLookup, RELATION, Section,
  useKoAndMunicipalities, useSpeciesOptions,
} from "./common";
import { ScannedAttachments } from "./ScanWizard";

interface MyCase {
  id: number; number: string; kind: string; status: string; status_label: string; bpg: string | null; data: CaseData;
  assigned_name: string | null; decision_note: string | null; submitted_at: string | null; decided_at: string | null;
  created_at: string; updated_at: string; opstina: string | null; events: CaseEvent[];
}

const PRIVACY = "Obavještenje o obradi ličnih podataka: Agencija za agrarna plaćanja obrađuje vaše ime, e-poštu, telefon, BPG i priložene dokaze isključivo radi provjere i vođenja podataka vašeg gazdinstva (ZZLP BiH 12/25). Podaci se ne ustupaju trećim licima i čuvaju se u skladu sa propisima o arhivskoj građi. Možete zatražiti uvid, ispravku ili brisanje podataka. JMBG se ne prikuplja.";

/** Eksterni korisnik: registracija i verifikacija sopstvenog gazdinstva (čarobnjak) i praćenje predmeta. */
export default function MyHoldingPage() {
  const { t } = useT();
  const user = useUser();
  const mine = useQuery({ queryKey: ["cases-mine"], queryFn: () => http.get<MyCase[]>("/api/v1/cases/mine") });
  const [mode, setMode] = useState<null | "new" | "edit">(null);
  const cases = mine.data ?? [];
  const cur = cases.find((c) => c.kind === "verifikacija_gazdinstva") ?? cases[0];

  if (mine.error) return <Alert color="red" icon={<IconAlertTriangle />}>{(mine.error as Error).message}</Alert>;
  if (mine.isLoading) return <Stack><Skeleton h={160} radius="lg" /><Skeleton h={360} radius="lg" /></Stack>;

  const empty: CaseData = {
    bpg: "", holding_name: "", holder_name: user.name ?? "", holding_type: "fizicko_lice", jib: "", relation: "nosilac",
    opstina_code: null, address: "", phone: "", email: user.email ?? "", parcels: [], orchards: [], message: "", consent: false,
  };

  let body: ReactNode;
  if (!cur || mode === "new") {
    body = (
      <>
        <IntroHero />
        <Wizard caseId={null} initial={cur && mode === "new" ? { ...cur.data, consent: false } : empty} resubmit={false}
          onDone={() => setMode(null)} onCancel={cur ? () => setMode(null) : undefined} />
      </>
    );
  } else if (cur.status === "nacrt") {
    body = (
      <>
        <IntroHero draft={cur.number} />
        <Wizard caseId={cur.id} initial={{ ...empty, ...cur.data }} resubmit={false} onDone={() => setMode(null)} />
      </>
    );
  } else if (mode === "edit" && cur.status === "dopuna") {
    body = (
      <>
        <Alert color="orange" variant="light" radius="lg" icon={<IconPencil />} title={t(`Dopuna zahtjeva ${cur.number}`)}>
          {t("Izmijenite podatke koje je referent tražio, priložite dokaz ako je potrebno i na posljednjem koraku pošaljite zahtjev ponovo.")}
        </Alert>
        <Wizard caseId={cur.id} initial={{ ...empty, ...cur.data }} resubmit onDone={() => setMode(null)} onCancel={() => setMode(null)} />
      </>
    );
  } else {
    body = <CaseStatusView caseId={cur.id} summary={cur} older={cases.filter((c) => c.id !== cur.id)}
      onEdit={() => setMode("edit")} onNew={() => setMode("new")} />;
  }
  return <Stack gap="md"><CasesStyle />{body}</Stack>;
}

// ============================================================================== uvod
function IntroHero({ draft }: { draft?: string }) {
  const { t } = useT();
  const user = useUser();
  return (
    <div className="case-hero case-fadein">
      <div className="apple-dot" />
      <Text size="sm" c="#cfe6c6">{draft ? t(`Nacrt ${draft} je sačuvan — nastavite gdje ste stali`) : t(`Zdravo, ${user.name ?? user.username}`)}</Text>
      <Title order={2} c="white" mt={4} maw={720} lh={1.2}>{t("Registracija gazdinstva")}</Title>
    </div>
  );
}

// ============================================================================== čarobnjak
const BPG_RE = /^[0-9]{6,15}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const blank = (v?: string | null) => (v && v.trim() ? v.trim() : null);
const pkey = (koId: number | null, n: string) => `${koId ?? ""}|${n}`;

function clean(d: CaseData): CaseData {
  return {
    ...d, bpg: blank(d.bpg), holding_name: blank(d.holding_name), holder_name: blank(d.holder_name),
    jib: d.holding_type === "fizicko_lice" ? null : blank(d.jib), address: blank(d.address), phone: blank(d.phone), email: blank(d.email),
    message: blank(d.message),
    orchards: d.orchards.map((o) => ({ ...o, name: blank(o.name), cultivar: blank(o.cultivar), rootstock: blank(o.rootstock) })),
  };
}

function Wizard({ caseId, initial, resubmit, onDone, onCancel }: {
  caseId: number | null; initial: CaseData; resubmit: boolean; onDone: () => void; onCancel?: () => void;
}) {
  const { t } = useT();
  const qc = useQueryClient();
  const [id, setId] = useState<number | null>(caseId);
  const [f, setF] = useState<CaseData>(initial);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState<Record<number, boolean>>({});
  const [lookups, setLookups] = useState<Record<string, ParcelLookup | "loading">>({});
  const set = <K extends keyof CaseData>(k: K, v: CaseData[K]) => setF((x) => ({ ...x, [k]: v }));
  const topRef = useRef<HTMLDivElement>(null);

  // provjera već unesenih čestica (nastavak nacrta / dopuna)
  useEffect(() => {
    for (const p of initial.parcels) {
      if (!p.ko_id) continue;
      const k = pkey(p.ko_id, p.number);
      setLookups((l) => ({ ...l, [k]: "loading" }));
      lookupParcel(p.ko_id, p.number).then((r) => setLookups((l) => ({ ...l, [k]: r }))).catch(() => setLookups((l) => ({ ...l, [k]: { found: false } })));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const errs = useMemo(() => {
    const e: Record<string, string> = {};
    if (!f.bpg || !BPG_RE.test(f.bpg)) e.bpg = "BPG ima 6–15 cifara";
    if (!blank(f.holder_name)) e.holder_name = "Unesite ime i prezime nosioca";
    if (f.holding_type !== "fizicko_lice" && !blank(f.jib)) e.jib = "JIB je obavezan za pravno lice i preduzetnika";
    if (f.holding_type !== "fizicko_lice" && blank(f.jib) && !/^[0-9]{12,13}$/.test(f.jib!.trim())) e.jib = "JIB ima 12 ili 13 cifara";
    if (!f.opstina_code) e.opstina_code = "Izaberite opštinu";
    if (!blank(f.phone)) e.phone = "Telefon je obavezan";
    if (blank(f.email) && !EMAIL_RE.test(f.email!.trim())) e.email = "Neispravna adresa e-pošte";
    return e;
  }, [f]);
  const step0ok = Object.keys(errs).length === 0;
  const step1errs = useMemo(() => {
    const e: string[] = [];
    if (!f.parcels.length) e.push("Unesite bar jednu katastarsku česticu");
    f.orchards.forEach((o, i) => {
      if (!o.species) e.push(`Voćnjak ${i + 1}: izaberite vrstu voća`);
      if (!o.parcels.length) e.push(`Voćnjak ${i + 1}: izaberite čestice na kojima se nalazi`);
    });
    return e;
  }, [f]);

  async function persist(data = f): Promise<number> {
    const body = clean(data);
    if (id) {
      await http.put(`/api/v1/cases/${id}`, body);
      return id;
    }
    const r = await http.post<{ id: number }>("/api/v1/cases", { kind: "verifikacija_gazdinstva", data: body });
    setId(r.id);
    qc.invalidateQueries({ queryKey: ["cases-mine"] });
    return r.id;
  }

  const go = async (to: number) => {
    setError(null);
    if (to > step) {
      setTried((x) => ({ ...x, [step]: true }));
      if (step === 0 && !step0ok) return;
      if (step === 1 && step1errs.length) return;
    }
    setBusy(true);
    try {
      if (step <= 1 || !id) await persist();
      setStep(to);
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) {
      setError(errMsg(e));
    } finally { setBusy(false); }
  };

  const saveDraft = async () => {
    setBusy(true); setError(null);
    try {
      await persist();
      notifications.show({ color: "green", icon: <IconDeviceFloppy size={16} />, message: t("Nacrt je sačuvan") });
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const submit = async () => {
    setTried((x) => ({ ...x, 3: true }));
    if (!f.consent) return;
    setBusy(true); setError(null);
    try {
      const cid = await persist();
      await http.post(`/api/v1/cases/${cid}/submit`);
      notifications.show({ color: "green", icon: <IconCheck size={16} />, title: t(resubmit ? "Zahtjev je ponovo poslat" : "Zahtjev je poslat na verifikaciju"),
        message: t("Obavijestićemo vas o svakoj promjeni statusa (i e-poštom).") });
      await qc.invalidateQueries({ queryKey: ["cases-mine"] });
      qc.invalidateQueries({ queryKey: ["case", String(cid)] });
      onDone();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Paper withBorder radius="lg" p={{ base: "sm", sm: "lg" }} ref={topRef} className="case-fadein">
      <Stepper active={step} onStepClick={(s) => s < step && go(s)} allowNextStepsSelect={false} size="sm" color="agri" mb="lg"
        styles={{ steps: { flexWrap: "wrap", rowGap: 8 } }}>
        <Stepper.Step label={t("Gazdinstvo")} description={t("osnovni podaci")} icon={<IconBuildingCommunity size={18} />} />
        <Stepper.Step label={t("Parcele i voćnjaci")} description={t("katastar")} icon={<IconMapPin size={18} />} />
        <Stepper.Step label={t("Dokazi")} description={t("prilozi")} icon={<IconFileCertificate size={18} />} />
        <Stepper.Step label={t("Pregled i slanje")} description={t("saglasnost")} icon={<IconSend size={18} />} />
      </Stepper>

      {step === 0 && <StepHolding f={f} set={set} errs={tried[0] ? errs : {}} />}
      {step === 1 && <StepParcels f={f} setF={setF} lookups={lookups} setLookups={setLookups} errs={tried[1] ? step1errs : []} />}
      {step === 2 && id && <StepDocs caseId={id} />}
      {step === 3 && <StepReview f={f} set={set} lookups={lookups} caseId={id} consentErr={!!tried[3] && !f.consent} />}

      {error && (
        <Alert color="red" variant="light" mt="md" icon={<IconAlertTriangle size={18} />} title={t("Nije sačuvano")} withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Group justify="space-between" mt="xl" wrap="wrap" gap="sm">
        <Group gap="xs">
          {step > 0 && <Button variant="default" leftSection={<IconArrowLeft size={16} />} onClick={() => go(step - 1)} disabled={busy}>{t("Nazad")}</Button>}
          {onCancel && step === 0 && <Button variant="subtle" color="gray" onClick={onCancel}>{t("Odustani")}</Button>}
        </Group>
        <Group gap="xs">
          {step < 3 && (
            <Button variant="subtle" leftSection={<IconDeviceFloppy size={16} />} onClick={saveDraft} loading={busy && step !== 3} disabled={!blank(f.holder_name)}>
              {t("Sačuvaj nacrt")}
            </Button>
          )}
          {step < 3 ? (
            <Button rightSection={<IconArrowRight size={16} />} onClick={() => go(step + 1)} loading={busy}>{t("Dalje")}</Button>
          ) : (
            <Button color="agri" size="md" leftSection={<IconSend size={18} />} onClick={submit} loading={busy}>
              {t(resubmit ? "Pošalji ponovo" : "Pošalji na verifikaciju")}
            </Button>
          )}
        </Group>
      </Group>
    </Paper>
  );
}

// ---------------------------------------------------------------------------- korak 1
function StepHolding({ f, set, errs }: { f: CaseData; set: <K extends keyof CaseData>(k: K, v: CaseData[K]) => void; errs: Record<string, string> }) {
  const { t } = useT();
  const { municipalities } = useKoAndMunicipalities();
  const e = (k: string) => (errs[k] ? t(errs[k]) : undefined);
  return (
    <Stack gap="md">
      <Grid gap="md">
        <Grid.Col span={{ base: 12, sm: 5 }}>
          <TextInput label="BPG" required value={f.bpg ?? ""} inputMode="numeric" maxLength={15} error={e("bpg")}
            description={t("Broj poljoprivrednog gazdinstva iz rješenja o upisu u RPG")}
            onChange={(ev) => set("bpg", ev.currentTarget.value.replace(/\D/g, ""))} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 7 }}>
          <TextInput label={t("Naziv gazdinstva")} placeholder={t("npr. PG Petrović")} value={f.holding_name ?? ""}
            description={t("Kako ste upisani u Registar (ako postoji naziv)")} onChange={(ev) => set("holding_name", ev.currentTarget.value)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label={t("Nosilac gazdinstva (ime i prezime / naziv)")} required value={f.holder_name ?? ""} error={e("holder_name")}
            onChange={(ev) => set("holder_name", ev.currentTarget.value)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select label={t("Moj odnos prema gazdinstvu")} value={f.relation} allowDeselect={false}
            onChange={(v) => v && set("relation", v as CaseData["relation"])}
            data={Object.entries(RELATION).map(([value, label]) => ({ value, label: t(label) }))} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select label={t("Vrsta lica")} value={f.holding_type} allowDeselect={false}
            onChange={(v) => v && set("holding_type", v as CaseData["holding_type"])}
            data={Object.entries(HOLDING_TYPE).map(([value, label]) => ({ value, label: t(label) }))} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          {f.holding_type !== "fizicko_lice" ? (
            <TextInput label="JIB" required value={f.jib ?? ""} error={e("jib")} inputMode="numeric" maxLength={13}
              description={t("Jedinstveni identifikacioni broj (12 ili 13 cifara)")} onChange={(ev) => set("jib", ev.currentTarget.value.replace(/\D/g, ""))} />
          ) : (
            <Alert variant="light" color="gray" icon={<IconInfoCircle size={16} />} p="xs" mt={{ base: 0, sm: 24 }}>
              <Text size="xs">{t("Za fizička lica JIB nije potreban. JMBG se ne prikuplja.")}</Text>
            </Alert>
          )}
        </Grid.Col>
        {f.relation === "ovlasceno_lice" && (
          <Grid.Col span={12}>
            <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />}>
              {t("Kao ovlašćeno lice, u koraku „Dokazi“ priložite ovlašćenje nosioca gazdinstva.")}
            </Alert>
          </Grid.Col>
        )}
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Select label={t("Opština")} required searchable value={f.opstina_code ?? null} error={e("opstina_code")}
            onChange={(v) => set("opstina_code", v)} data={municipalities} nothingFoundMessage={t("Nema rezultata")} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label={t("Adresa")} value={f.address ?? ""} placeholder={t("Ulica i broj, mjesto")}
            onChange={(ev) => set("address", ev.currentTarget.value)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label={t("Telefon")} required value={f.phone ?? ""} error={e("phone")} placeholder="+387 65 ..." type="tel"
            onChange={(ev) => set("phone", ev.currentTarget.value)} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label={t("E-pošta")} value={f.email ?? ""} error={e("email")} type="email"
            description={t("Na ovu adresu stižu obavještenja o predmetu")} onChange={(ev) => set("email", ev.currentTarget.value)} />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

// ---------------------------------------------------------------------------- korak 2
function StepParcels({ f, setF, lookups, setLookups, errs }: {
  f: CaseData; setF: Dispatch<SetStateAction<CaseData>>; lookups: Record<string, ParcelLookup | "loading">;
  setLookups: Dispatch<SetStateAction<Record<string, ParcelLookup | "loading">>>; errs: string[];
}) {
  const { t } = useT();
  const { ko, loading } = useKoAndMunicipalities();
  const species = useSpeciesOptions();
  const [koId, setKoId] = useState<string | null>(null);
  const [num, setNum] = useState("");
  const [adding, setAdding] = useState(false);
  const koList = useMemo(() => {
    const own = ko.filter((k) => !f.opstina_code || k.opstina_code === f.opstina_code);
    const list = own.length ? own : ko;
    return list.map((k) => ({ value: String(k.id), label: f.opstina_code && own.length ? k.name : `${k.name} (${k.opstina ?? ""})` }));
  }, [ko, f.opstina_code]);
  useEffect(() => { if (!koId && koList.length === 1) setKoId(koList[0].value); }, [koList, koId]);

  const add = async () => {
    if (!koId) return;
    const k = ko.find((x) => String(x.id) === koId);
    const nums = num.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    if (!nums.length || !k) return;
    setAdding(true);
    try {
      for (const n of nums) {
        const r = await lookupParcel(k.id, n);
        const key = pkey(k.id, r.number);
        setLookups((l) => ({ ...l, [key]: r }));
        setF((x) => x.parcels.some((p) => p.ko_id === k.id && p.number === r.number) ? x : {
          ...x, parcels: [...x.parcels, { ko_id: k.id, ko_name: k.name, number: r.number, parcel_id: r.found ? r.id ?? null : null, use: "vocnjak" }],
        });
        if (!r.found) notifications.show({ color: "orange", message: t(`KČ ${n} nije pronađena u KO ${k.name} — provjerite broj (npr. 1234/2)`) });
      }
      setNum("");
    } catch (e) {
      notifications.show({ color: "red", message: errMsg(e) });
    } finally { setAdding(false); }
  };

  const removeParcel = (i: number) => setF((x) => {
    const p = x.parcels[i];
    return { ...x, parcels: x.parcels.filter((_, j) => j !== i), orchards: x.orchards.map((o) => ({ ...o, parcels: o.parcels.filter((n) => n !== p.number) })) };
  });
  const setOrchard = (i: number, patch: Partial<OrchardIn>) => setF((x) => ({ ...x, orchards: x.orchards.map((o, j) => (j === i ? { ...o, ...patch } : o)) }));
  const addOrchard = () => setF((x) => ({
    ...x, orchards: [...x.orchards, {
      name: "", species: "JABUKA", cultivar: "", rootstock: "", planting_year: null, row_spacing_m: null, tree_spacing_m: null, trees: null,
      organic: false, hail_net: false, irrigation: null,
      parcels: x.orchards.length ? [] : x.parcels.filter((p) => p.use === "vocnjak").map((p) => p.number),
    }],
  }));

  const mapItems = f.parcels.map((p) => {
    const l = lookups[pkey(p.ko_id, p.number)];
    return l && l !== "loading" && l.found ? { geojson: l.geojson, label: p.number } : null;
  }).filter(Boolean) as { geojson: unknown; label: string }[];
  const parcelOpts = f.parcels.map((p) => ({ value: p.number, label: `${p.number} — ${p.ko_name ?? ""}` }));
  const areaOf = (nums: string[]) => f.parcels.filter((p) => nums.includes(p.number)).reduce((s, p) => {
    const l = lookups[pkey(p.ko_id, p.number)];
    return s + (l && l !== "loading" && l.found ? l.area_m2 ?? 0 : 0);
  }, 0);
  const totalArea = areaOf(f.parcels.map((p) => p.number));
  const thisYear = new Date().getFullYear();

  return (
    <Stack gap="lg">
      <div>
        <Group gap={8} mb={4}><IconMapPin size={18} color="var(--mantine-color-agri-7)" /><Text fw={750}>{t("Katastarske čestice")}</Text></Group>
        <Text size="sm" c="dimmed" mb="sm">{t("Izaberite katastarsku opštinu i upišite broj čestice (više brojeva odvojite zarezom). Svaka čestica se odmah provjerava u katastru.")}</Text>
        <Paper p="sm" radius="md" style={{ background: "var(--mantine-color-default-hover)" }}>
          <Grid gap="sm" align="flex-end">
            <Grid.Col span={{ base: 12, sm: 5 }}>
              <Select label={t("Katastarska opština (KO)")} searchable value={koId} onChange={setKoId} data={koList} placeholder={loading ? t("Učitavanje...") : t("Izaberite KO")}
                nothingFoundMessage={t("Nema KO za izabranu opštinu")} limit={300} />
            </Grid.Col>
            <Grid.Col span={{ base: 8, sm: 4 }}>
              <TextInput label={t("Broj KČ")} placeholder="1234/2, 1235" value={num} onChange={(e) => setNum(e.currentTarget.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
            </Grid.Col>
            <Grid.Col span={{ base: 4, sm: 3 }}>
              <Button fullWidth leftSection={<IconPlus size={16} />} onClick={add} loading={adding} disabled={!koId || !num.trim()}>{t("Dodaj")}</Button>
            </Grid.Col>
          </Grid>
        </Paper>
      </div>

      {f.parcels.length > 0 && (
        <Grid gap="md">
          <Grid.Col span={{ base: 12, md: mapItems.length ? 7 : 12 }}>
            <ScrollArea type="auto" scrollbars="x">
              <Table verticalSpacing={6} fz="sm" miw={520} highlightOnHover>
                <Table.Thead><Table.Tr>
                  <Table.Th>KO</Table.Th><Table.Th>KČ</Table.Th><Table.Th>{t("Katastar")}</Table.Th><Table.Th w={130}>{t("Koristi se kao")}</Table.Th><Table.Th w={36} />
                </Table.Tr></Table.Thead>
                <Table.Tbody>
                  {f.parcels.map((p, i) => {
                    const l = lookups[pkey(p.ko_id, p.number)];
                    return (
                      <Table.Tr key={pkey(p.ko_id, p.number)} className="case-fadein">
                        <Table.Td>{p.ko_name}</Table.Td>
                        <Table.Td fw={700} ff="monospace">{p.number}</Table.Td>
                        <Table.Td>
                          {l === "loading" || !l ? <Loader size="xs" /> : l.found ? (
                            <Group gap={6} wrap="nowrap">
                              <Badge color="green" variant="light" leftSection={<IconCircleCheck size={12} />}>{t("U katastru")}</Badge>
                              <Text size="xs" c="dimmed">{fmtArea(l.area_m2)}{l.land_use ? ` · ${l.land_use}` : ""}</Text>
                            </Group>
                          ) : (
                            <Tooltip label={t("Provjerite broj i KO. Referent će česticu provjeriti ručno.")} withArrow>
                              <Badge color="orange" variant="light" leftSection={<IconAlertTriangle size={12} />}>{t("Nije pronađena")}</Badge>
                            </Tooltip>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Select size="xs" value={p.use ?? null} allowDeselect={false}
                            data={Object.entries(PARCEL_USE).map(([value, label]) => ({ value, label: t(label) }))}
                            onChange={(v) => setF((x) => ({ ...x, parcels: x.parcels.map((q, j) => (j === i ? { ...q, use: v } : q)) }))} />
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon variant="subtle" color="red" onClick={() => removeParcel(i)} aria-label={t("Ukloni")}><IconTrash size={16} /></ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            <Text size="xs" c="dimmed" mt={6}>{t(`Ukupno čestica: ${f.parcels.length}, površina pronađenih: ${fmtArea(totalArea)}`)}</Text>
          </Grid.Col>
          {mapItems.length > 0 && <Grid.Col span={{ base: 12, md: 5 }}><ParcelMap items={mapItems} height={260} /></Grid.Col>}
        </Grid>
      )}

      <div>
        <Group justify="space-between" mb="sm">
          <Group gap={8}><IconTrees size={18} color="var(--mantine-color-agri-7)" /><Text fw={750}>{t("Voćnjaci")}</Text>
            <Badge variant="light">{f.orchards.length}</Badge></Group>
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addOrchard} disabled={!f.parcels.length}>{t("Dodaj voćnjak")}</Button>
        </Group>
        {!f.orchards.length && (
          <Text size="sm" c="dimmed">{t(f.parcels.length ? "Opišite zasade na unesenim česticama — referent ih na osnovu toga ucrtava kao voćnjake gazdinstva." : "Prvo unesite čestice, zatim dodajte voćnjake na njima.")}</Text>
        )}
        <Stack gap="sm">
          {f.orchards.map((o, i) => {
            const a = areaOf(o.parcels);
            const est = a && o.row_spacing_m && o.tree_spacing_m ? Math.round(a / (o.row_spacing_m * o.tree_spacing_m)) : null;
            return (
              <Paper key={i} withBorder radius="md" p="sm" className="case-fadein" style={{ borderLeft: `5px solid ${speciesColor(o.species)}` }}>
                <Group justify="space-between" mb="xs">
                  <Text fw={700}>{t(`Voćnjak ${i + 1}`)}{a ? <Text span size="xs" c="dimmed" fw={500}> · {fmtArea(a)}</Text> : null}</Text>
                  <ActionIcon variant="subtle" color="red" onClick={() => setF((x) => ({ ...x, orchards: x.orchards.filter((_, j) => j !== i) }))} aria-label={t("Ukloni voćnjak")}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
                <Grid gap="xs">
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Select label={t("Vrsta voća")} required searchable value={o.species} data={species.length ? species : [{ value: "JABUKA", label: "Jabuka" }]}
                      onChange={(v) => setOrchard(i, { species: v ?? "" })} allowDeselect={false} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, sm: 4 }}>
                    <TextInput label={t("Sorta")} placeholder="Idared" value={o.cultivar ?? ""} onChange={(e) => setOrchard(i, { cultivar: e.currentTarget.value })} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, sm: 4 }}>
                    <TextInput label={t("Podloga")} placeholder="M9" value={o.rootstock ?? ""} onChange={(e) => setOrchard(i, { rootstock: e.currentTarget.value })} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, sm: 3 }}>
                    <NumberInput label={t("Godina sadnje")} min={1950} max={thisYear} allowDecimal={false} value={o.planting_year ?? ""}
                      onChange={(v) => setOrchard(i, { planting_year: v === "" ? null : Number(v) })} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, sm: 3 }}>
                    <NumberInput label={t("Razmak redova (m)")} min={0.5} max={15} step={0.1} decimalScale={2} value={o.row_spacing_m ?? ""}
                      onChange={(v) => setOrchard(i, { row_spacing_m: v === "" ? null : Number(v) })} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, sm: 3 }}>
                    <NumberInput label={t("Razmak u redu (m)")} min={0.2} max={15} step={0.1} decimalScale={2} value={o.tree_spacing_m ?? ""}
                      onChange={(v) => setOrchard(i, { tree_spacing_m: v === "" ? null : Number(v) })} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, sm: 3 }}>
                    <NumberInput label={t("Broj stabala")} min={0} allowDecimal={false} thousandSeparator="." decimalSeparator="," value={o.trees ?? ""}
                      description={est ? `${t("procjena")} ≈ ${fmtNum(est, 0)}` : undefined}
                      onChange={(v) => setOrchard(i, { trees: v === "" ? null : Number(v) })} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <MultiSelect label={t("Na česticama")} required data={parcelOpts} value={o.parcels} placeholder={t("Izaberite KČ")}
                      onChange={(v) => setOrchard(i, { parcels: v })} error={!o.parcels.length && errs.length ? t("Izaberite bar jednu česticu") : undefined} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 3 }}>
                    <Select label={t("Navodnjavanje")} clearable value={o.irrigation ?? null} onChange={(v) => setOrchard(i, { irrigation: v })}
                      data={["kap po kap", "orošavanje", "mikrorasprskivači", "bez navodnjavanja"].map((x) => ({ value: x, label: t(x) }))} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 3 }}>
                    <Stack gap={8} mt={{ base: 0, sm: 26 }}>
                      <Switch size="sm" label={t("Organska proizvodnja")} checked={o.organic} onChange={(e) => setOrchard(i, { organic: e.currentTarget.checked })} />
                      <Switch size="sm" label={t("Protivgradna mreža")} checked={o.hail_net} onChange={(e) => setOrchard(i, { hail_net: e.currentTarget.checked })} />
                    </Stack>
                  </Grid.Col>
                </Grid>
              </Paper>
            );
          })}
        </Stack>
      </div>

      {errs.length > 0 && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />}>
          <Stack gap={2}>{errs.map((e) => <Text key={e} size="sm">{t(e)}</Text>)}</Stack>
        </Alert>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------- korak 3
const DOC_TYPES = ["Rješenje o upisu u RPG", "Ovlašćenje nosioca gazdinstva", "Posjedovni list / ugovor o zakupu", "Fotografija zasada", "Ostalo"];

function DocsPanel({ caseId, canUpload = true, compact = false }: { caseId: number; canUpload?: boolean; compact?: boolean }) {
  const { t } = useT();
  const qc = useQueryClient();
  const d = useQuery({ queryKey: ["case", String(caseId)], queryFn: () => http.get<CaseDetail>(`/api/v1/cases/${caseId}`) });
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<string | null>(DOC_TYPES[0]);
  const [busy, setBusy] = useState(false);
  const docs = d.data?.documents ?? [];
  const upload = async () => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { notifications.show({ color: "red", message: t("Fajl je veći od 20 MB") }); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", `${kind ?? "Prilog"} — ${file.name}`);
      await http.form(`/api/v1/cases/${caseId}/documents`, fd);
      setFile(null);
      notifications.show({ color: "green", icon: <IconCheck size={16} />, message: t("Dokument je priložen") });
      qc.invalidateQueries({ queryKey: ["case", String(caseId)] });
      qc.invalidateQueries({ queryKey: ["cases-mine"] });
    } catch (e) { notifications.show({ color: "red", message: errMsg(e) }); } finally { setBusy(false); }
  };
  return (
    <Stack gap="sm">
      {canUpload && (
        <Paper p="sm" radius="md" style={{ background: "var(--mantine-color-default-hover)" }}>
          <Grid gap="sm" align="flex-end">
            <Grid.Col span={{ base: 12, sm: compact ? 12 : 4 }}>
              <Select label={t("Vrsta dokumenta")} value={kind} onChange={setKind} data={DOC_TYPES.map((x) => ({ value: x, label: t(x) }))} allowDeselect={false} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: compact ? 8 : 5 }}>
              <FileInput label={t("Fajl")} placeholder={t("PDF, JPG ili PNG do 20 MB")} value={file} onChange={setFile} clearable
                accept=".pdf,.jpg,.jpeg,.png" leftSection={<IconUpload size={15} />} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: compact ? 4 : 3 }}>
              <Button fullWidth leftSection={<IconUpload size={16} />} disabled={!file} loading={busy} onClick={upload}>{t("Priloži")}</Button>
            </Grid.Col>
          </Grid>
        </Paper>
      )}
      {d.isLoading ? <Skeleton h={40} /> : !docs.length ? (
        <Text size="sm" c="dimmed">{t("Još nema priloženih dokumenata.")}</Text>
      ) : docs.map((doc) => (
        <Group key={doc.id} justify="space-between" wrap="nowrap" p="xs" className="case-fadein"
          style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: 10 }}>
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <ThemeIcon variant="light" color="soil" radius="md"><IconFile size={16} /></ThemeIcon>
            <div style={{ minWidth: 0 }}>
              <Text size="sm" fw={600} truncate>{doc.title}</Text>
              <Text size="xs" c="dimmed" truncate>{doc.filename} · {fmtSize(doc.size_bytes)} · {fmtDateTime(doc.created_at)}</Text>
            </div>
          </Group>
          <ActionIcon variant="subtle" onClick={() => openDocument(caseId, doc, (m) => notifications.show({ color: "red", message: m }))} aria-label={t("Otvori")}>
            <IconDownload size={16} />
          </ActionIcon>
        </Group>
      ))}
    </Stack>
  );
}

function StepDocs({ caseId }: { caseId: number }) {
  const { t } = useT();
  return (
    <Stack gap="sm">
      <Group gap={8}><IconFileCertificate size={18} color="var(--mantine-color-agri-7)" /><Text fw={750}>{t("Dokazi")}</Text></Group>
      <Text size="sm" c="dimmed">
        {t("Priložite rješenje o upisu u Registar poljoprivrednih gazdinstava. Ako niste nosilac, priložite i ovlašćenje nosioca. Fotografija ili sken su dovoljni ako su čitljivi.")}
      </Text>
      <DocsPanel caseId={caseId} />
      <Paper withBorder p="sm" radius="md">
        <ScannedAttachments caseId={caseId} holdingId={null} canScan canClassify={false} />
      </Paper>
    </Stack>
  );
}

// ---------------------------------------------------------------------------- korak 4
function DataSummary({ f, lookups }: { f: CaseData; lookups?: Record<string, ParcelLookup | "loading"> }) {
  const { t } = useT();
  const { municipalities } = useKoAndMunicipalities();
  const sp = useSpeciesNames();
  const opstina = municipalities.find((m) => m.value === f.opstina_code)?.label ?? f.opstina_code ?? "—";
  const kv: [string, ReactNode][] = [
    ["BPG", f.bpg || "—"], ["Naziv", f.holding_name || "—"], ["Nosilac", f.holder_name || "—"],
    ["Vrsta lica", t(HOLDING_TYPE[f.holding_type] ?? f.holding_type)], ...(f.jib ? [["JIB", f.jib] as [string, ReactNode]] : []),
    ["Odnos", t(RELATION[f.relation] ?? f.relation)], ["Opština", opstina], ["Adresa", f.address || "—"], ["Telefon", f.phone || "—"], ["E-pošta", f.email || "—"],
  ];
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="sm">
        {kv.map(([k, v]) => <div key={k}><Text size="xs" c="dimmed">{t(k)}</Text><Text size="sm" fw={650} style={{ wordBreak: "break-word" }}>{v}</Text></div>)}
      </SimpleGrid>
      <div>
        <Text size="xs" c="dimmed" mb={4}>{t("Katastarske čestice")} ({f.parcels.length})</Text>
        <Group gap={6}>
          {f.parcels.map((p) => {
            const l = lookups?.[pkey(p.ko_id, p.number)];
            const ok = l && l !== "loading" ? l.found : undefined;
            return (
              <Badge key={pkey(p.ko_id, p.number)} variant="light" size="lg" radius="sm" tt="none" fw={600} color={ok === undefined ? "gray" : ok ? "green" : "orange"}
                leftSection={ok === undefined ? undefined : ok ? <IconCheck size={12} /> : <IconAlertTriangle size={12} />}>
                {p.ko_name} {p.number}{l && l !== "loading" && l.found ? ` · ${fmtArea(l.area_m2)}` : ""}
              </Badge>
            );
          })}
        </Group>
      </div>
      {f.orchards.length > 0 && (
        <div>
          <Text size="xs" c="dimmed" mb={4}>{t("Voćnjaci")} ({f.orchards.length})</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            {f.orchards.map((o, i) => (
              <Paper key={i} withBorder p="xs" radius="md" style={{ borderLeft: `4px solid ${speciesColor(o.species)}` }}>
                <Text size="sm" fw={700}>{o.name || t(sp(o.species))}{o.cultivar ? ` — ${o.cultivar}` : ""}</Text>
                <Text size="xs" c="dimmed">
                  {[o.planting_year && `${o.planting_year}.`, o.trees && `${fmtNum(o.trees, 0)} ${t("stabala")}`,
                    o.row_spacing_m && o.tree_spacing_m && `${o.row_spacing_m} × ${o.tree_spacing_m} m`, o.organic && t("organski"),
                    o.hail_net && t("mreža")].filter(Boolean).join(" · ")}
                </Text>
                <Text size="xs" c="dimmed">KČ: {o.parcels.join(", ") || "—"}</Text>
              </Paper>
            ))}
          </SimpleGrid>
        </div>
      )}
    </Stack>
  );
}

function StepReview({ f, set, lookups, caseId, consentErr }: {
  f: CaseData; set: <K extends keyof CaseData>(k: K, v: CaseData[K]) => void; lookups: Record<string, ParcelLookup | "loading">;
  caseId: number | null; consentErr: boolean;
}) {
  const { t } = useT();
  const notice = useQuery({ queryKey: ["privacy-notice"], staleTime: 600_000, queryFn: () => http.get<string>("/api/v1/cases/lookup/notice") });
  const d = useQuery({ queryKey: ["case", String(caseId)], enabled: !!caseId, queryFn: () => http.get<CaseDetail>(`/api/v1/cases/${caseId}`) });
  const nf = f.parcels.filter((p) => { const l = lookups[pkey(p.ko_id, p.number)]; return l && l !== "loading" && !l.found; }).length;
  const docs = d.data?.documents.length ?? 0;
  return (
    <Stack gap="md">
      <Group gap={8}><IconShieldCheck size={18} color="var(--mantine-color-agri-7)" /><Text fw={750}>{t("Provjerite podatke prije slanja")}</Text></Group>
      <Paper withBorder radius="md" p="md"><DataSummary f={f} lookups={lookups} /></Paper>
      <Group gap="xs">
        <Badge size="lg" variant="light" tt="none" color={docs ? "green" : "orange"} leftSection={<IconFile size={13} />}>{t(`Priloženih dokumenata: ${docs}`)}</Badge>
        {nf > 0 && <Badge size="lg" variant="light" tt="none" color="orange" leftSection={<IconAlertTriangle size={13} />}>{t(`${nf} KČ nije pronađeno u katastru`)}</Badge>}
      </Group>
      {!docs && <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>{t("Niste priložili dokaz — vratite se na korak „Dokazi“. Bez dokaza referent ne može potvrditi podatke.")}</Alert>}
      <Textarea label={t("Poruka referentu (opciono)")} autosize minRows={2} value={f.message ?? ""} onChange={(e) => set("message", e.currentTarget.value)}
        placeholder={t("Npr. dio zasada je na parceli u zakupu...")} />
      <Alert variant="light" color="blue" icon={<IconInfoCircle />} radius="md"><Text size="xs">{t(notice.data ?? PRIVACY)}</Text></Alert>
      <Checkbox checked={f.consent} onChange={(e) => set("consent", e.currentTarget.checked)} error={consentErr ? t("Saglasnost je obavezna") : undefined}
        label={t("Upoznat/a sam sa obavještenjem o obradi ličnih podataka i potvrđujem da su navedeni podaci tačni.")} />
    </Stack>
  );
}

// ============================================================================== praćenje predmeta
const NEXT: Record<string, string> = {
  podnesen: "Zahtjev je primljen. Rukovodilac ga dodjeljuje referentu Agencije — o tome ćete dobiti obavještenje.",
  dodijeljen: "Referent će provjeriti vaše podatke u Registru poljoprivrednih gazdinstava i katastru.",
  u_obradi: "Referent provjerava podatke. Ako nešto nedostaje, vratiće vam zahtjev na dopunu sa objašnjenjem.",
  dopuna: "Referent je tražio dopunu. Izmijenite podatke ili priložite dokaz i pošaljite zahtjev ponovo.",
  odobren: "Gazdinstvo je verifikovano i aktivno u sistemu.",
  odbijen: "Zahtjev je odbijen. Možete podnijeti novi zahtjev sa ispravnim podacima i dokazima.",
};

function CaseStatusView({ caseId, summary, older, onEdit, onNew }: {
  caseId: number; summary: MyCase; older: MyCase[]; onEdit: () => void; onNew: () => void;
}) {
  const { t } = useT();
  const user = useUser();
  const nav = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["case", String(caseId)], queryFn: () => http.get<CaseDetail>(`/api/v1/cases/${caseId}`), refetchInterval: 60_000 });
  const c = q.data;
  const status = c?.status ?? summary.status;
  const events = c?.events ?? summary.events;
  const lastReturn = [...events].reverse().find((e) => e.kind === "dopuna");
  const [busy, setBusy] = useState(false);
  const open = OPEN_STATUSES.includes(status);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["case", String(caseId)] }); qc.invalidateQueries({ queryKey: ["cases-mine"] }); };
  const hasAccess = !!summary.bpg && (user.bpg ?? []).includes(summary.bpg);

  const resend = async () => {
    setBusy(true);
    try {
      await http.post(`/api/v1/cases/${caseId}/submit`);
      notifications.show({ color: "green", icon: <IconCheck size={16} />, message: t("Zahtjev je ponovo poslat referentu") });
      refresh();
    } catch (e) { notifications.show({ color: "red", message: errMsg(e) }); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="case-hero case-fadein">
        <div className="apple-dot" />
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md" style={{ position: "relative" }}>
          <div style={{ minWidth: 0, flex: "1 1 340px" }}>
            <Text size="sm" c="#cfe6c6">{summary.number} · {t("Registracija i verifikacija gazdinstva")}{summary.bpg ? ` · BPG ${summary.bpg}` : ""}</Text>
            <Title order={2} c="white" mt={4} lh={1.15}>{t(summary.status_label && status === summary.status ? summary.status_label : status)}</Title>
            <Text c="#e3f1dd" mt={6} maw={680}>
              {t(NEXT[status] ?? "")}
              {status === "dodijeljen" && c?.due_date ? ` ${t("Rok za obradu")}: ${fmtDate(c.due_date).replace(/\.$/, "")}.` : ""}
            </Text>
            <CaseProgress status={status} />
          </div>
          <Stack gap={6} align="flex-start" style={{ flex: "0 1 auto" }}>
            <CaseStatusBadge status={status} variant="white" size="lg" />
            {summary.assigned_name && <Text size="xs" c="#cfe6c6">{t("Referent")}: {summary.assigned_name}</Text>}
            {summary.submitted_at && <Text size="xs" c="#cfe6c6">{t("Poslato")} {fmtDateTime(summary.submitted_at)}</Text>}
          </Stack>
        </Group>
      </div>

      {status === "dopuna" && (
        <Alert color="orange" variant="filled" radius="lg" icon={<IconArrowBackUp size={22} />} title={t("Referent traži dopunu zahtjeva")} className="case-fadein">
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{lastReturn?.note ?? ""}</Text>
          {lastReturn && <Text size="xs" mt={4} opacity={0.85}>{lastReturn.actor_name} · {fmtDateTime(lastReturn.ts)}</Text>}
          <Group mt="md" gap="xs">
            <Button color="white" variant="white" c="orange.8" leftSection={<IconPencil size={16} />} onClick={onEdit}>{t("Izmijeni podatke")}</Button>
            <Button color="orange.9" leftSection={<IconSend size={16} />} loading={busy} onClick={resend}>{t("Pošalji ponovo bez izmjena")}</Button>
          </Group>
        </Alert>
      )}

      {status === "odobren" && (
        <Paper radius="lg" p="lg" withBorder className="case-fadein" style={{ borderColor: "var(--mantine-color-green-5)", background: "rgba(64,192,87,.06)" }}>
          <Group wrap="nowrap" align="flex-start" gap="md">
            <ThemeIcon size={52} radius="xl" color="green"><IconCircleCheck size={30} /></ThemeIcon>
            <div style={{ flex: 1 }}>
              <Title order={4}>{t("Gazdinstvo je verifikovano")}</Title>
              <Text size="sm" mt={4}>
                {hasAccess ? t("Podaci vašeg gazdinstva su dostupni — voćnjaci, poljoprivredna knjiga i stanje zasada.")
                  : t("Da biste vidjeli svoje gazdinstvo, osvježite pristup (ponovna prijava preuzima nova ovlašćenja).")}
              </Text>
              {(c?.decision_note ?? summary.decision_note) && <Text size="sm" c="dimmed" mt={4}>„{c?.decision_note ?? summary.decision_note}“</Text>}
              <Group mt="md" gap="xs">
                {hasAccess && c?.holding_id ? (
                  <Button leftSection={<IconHome2 size={16} />} onClick={() => nav(`/gazdinstva/${c.holding_id}`)}>{t("Otvori moje gazdinstvo")}</Button>
                ) : (
                  <Button leftSection={<IconKey size={16} />} onClick={() => login()}>{t("Osvježi pristup")}</Button>
                )}
              </Group>
            </div>
          </Group>
        </Paper>
      )}

      {status === "odbijen" && (
        <Alert color="red" variant="light" radius="lg" icon={<IconCircleX size={22} />} title={t("Zahtjev je odbijen")}>
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{c?.decision_note ?? summary.decision_note}</Text>
          <Button mt="md" leftSection={<IconPlus size={16} />} onClick={onNew}>{t("Podnesi novi zahtjev")}</Button>
        </Alert>
      )}

      <Grid gap="md">
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap="md">
            <MessageThread caseId={caseId} events={events} canWrite={open} onSent={refresh} />
            <Section title="Podaci iz zahtjeva" icon={<IconBuildingCommunity size={17} />}
              right={c?.can_edit ? <Button size="xs" variant="light" leftSection={<IconPencil size={14} />} onClick={onEdit}>{t("Izmijeni")}</Button> : undefined}>
              <DataSummary f={c?.data ?? summary.data} />
            </Section>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Stack gap="md">
            <Section title="Prilozi" icon={<IconFile size={17} />}>
              <Stack gap="sm">
                <DocsPanel caseId={caseId} canUpload={open} compact />
                <ScannedAttachments caseId={caseId} holdingId={null} canScan={open} canClassify={false} />
              </Stack>
            </Section>
            <Section title="Tok predmeta" icon={<IconShieldCheck size={17} />}><CaseTimeline events={events} compact /></Section>
            {older.length > 0 && (
              <Section title="Raniji zahtjevi" icon={<IconFile size={17} />}>
                <Stack gap={6}>
                  {older.map((o) => (
                    <Group key={o.id} justify="space-between" wrap="nowrap">
                      <div><Text size="sm" fw={600} ff="monospace">{o.number}</Text><Text size="xs" c="dimmed">{fmtDate(o.created_at)}</Text></div>
                      <CaseStatusBadge status={o.status} />
                    </Group>
                  ))}
                </Stack>
              </Section>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </>
  );
}

function MessageThread({ caseId, events, canWrite, onSent }: { caseId: number; events: CaseEvent[]; canWrite: boolean; onSent: () => void }) {
  const { t } = useT();
  const user = useUser();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const msgs = events.filter((e) => e.kind === "komentar" || e.kind === "dopuna");
  const send = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await http.post(`/api/v1/cases/${caseId}/comments`, { note: note.trim(), internal: false });
      setNote(""); onSent();
    } catch (e) { notifications.show({ color: "red", message: errMsg(e) }); } finally { setBusy(false); }
  };
  return (
    <Section title="Poruke" icon={<IconMessage size={17} />} right={<Badge variant="light">{msgs.length}</Badge>}>
      <Stack gap={8} mb={canWrite ? "sm" : 0}>
        {!msgs.length && <Text size="sm" c="dimmed">{t("Ovdje se razmjenjuju poruke sa referentom Agencije. Dobićete obavještenje kada referent odgovori.")}</Text>}
        {msgs.map((m, i) => {
          const mine = m.actor ? m.actor === user.username : m.actor_name === (user.name ?? user.username);
          return (
            <div key={m.id ?? i} className={`case-bubble ${mine ? "mine" : "theirs"} case-fadein`}>
              {m.kind === "dopuna" && <Badge size="xs" color="orange" mb={4}>{t("Zahtjev za dopunu")}</Badge>}
              <Text size="sm">{m.note}</Text>
              <Text size="10px" mt={4} opacity={0.75}>{mine ? t("Vi") : m.actor_name} · {fmtDateTime(m.ts)}</Text>
            </div>
          );
        })}
      </Stack>
      {canWrite && (
        <Group gap="xs" align="flex-end" wrap="nowrap">
          <Textarea style={{ flex: 1 }} autosize minRows={1} maxRows={5} placeholder={t("Napišite poruku referentu...")} value={note}
            onChange={(e) => setNote(e.currentTarget.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }} />
          <ActionIcon size={36} radius="md" variant="filled" onClick={send} loading={busy} disabled={!note.trim()} aria-label={t("Pošalji")}>
            <IconSend size={17} />
          </ActionIcon>
        </Group>
      )}
    </Section>
  );
}
